import {
  AMAP_CHARGING_VEHICLE_START,
  formatStationDistance,
  type GeoPoint
} from "./amapChargingDemo";

export type ParcelPointSource = "loading" | "amap" | "demo";

export interface ParcelPickupPoint {
  id: string;
  name: string;
  shortName: string;
  address: string;
  location: GeoPoint;
  distanceM: number;
  pendingCount: number;
  routeMinutes: number;
  routeDistanceKm: number;
  detourKm: number;
  eta: string;
  tag?: string;
  source: Exclude<ParcelPointSource, "loading">;
}

const FALLBACK_PARCEL_TEMPLATES = [
  {
    id: "yun-jin-west-pickup",
    name: "云锦西路驿站",
    shortName: "云锦西路驿站",
    address: "当前位置东侧取件点",
    offsetLng: 0.0036,
    offsetLat: 0.0012,
    detourKm: 0.4,
    tag: "推荐"
  },
  {
    id: "cainiao-office-pickup",
    name: "办公区菜鸟驿站",
    shortName: "菜鸟驿站",
    address: "当前位置北侧取件点",
    offsetLng: -0.0025,
    offsetLat: 0.0027,
    detourKm: 0.6,
    tag: undefined
  },
  {
    id: "fengchao-locker",
    name: "丰巢智能柜",
    shortName: "丰巢智能柜",
    address: "当前位置西南侧取件点",
    offsetLng: -0.0031,
    offsetLat: -0.002,
    detourKm: 0.8,
    tag: undefined
  }
] as const;

export function createFallbackParcelPickupPoints(
  vehicleLocation: GeoPoint = AMAP_CHARGING_VEHICLE_START.location,
  pendingCount = 2
): ParcelPickupPoint[] {
  return rankParcelPickupPoints(
    FALLBACK_PARCEL_TEMPLATES.map((template) => {
      const location = {
        lng: vehicleLocation.lng + template.offsetLng,
        lat: vehicleLocation.lat + template.offsetLat
      };
      const distanceM = getApproxDistanceM(vehicleLocation, location);
      const routeDistanceKm = Math.max(0.3, distanceM / 1000 * 1.2);
      const routeMinutes = estimateParcelRouteMinutes(routeDistanceKm);

      return {
        id: template.id,
        name: template.name,
        shortName: template.shortName,
        address: template.address,
        location,
        distanceM,
        pendingCount,
        routeMinutes,
        routeDistanceKm,
        detourKm: template.detourKm,
        eta: formatParcelEta(routeMinutes),
        tag: template.tag,
        source: "demo" as const
      };
    })
  );
}

export function formatPickupDistance(distanceM: number) {
  return formatStationDistance(distanceM);
}

export function getPickupPointDisplayName(point: ParcelPickupPoint) {
  return point.shortName || point.name;
}

export function getRecommendedParcelPickupPoint(points: ParcelPickupPoint[]) {
  return points.find((point) => point.tag === "推荐") ?? points[0] ?? createFallbackParcelPickupPoints()[0];
}

export function rankParcelPickupPoints(points: ParcelPickupPoint[]) {
  return [...points]
    .sort((a, b) => getParcelPointScore(b) - getParcelPointScore(a) || a.distanceM - b.distanceM)
    .map((point, index) => ({
      ...point,
      tag: index === 0 ? "推荐" : undefined
    }));
}

export function getApproxDistanceM(from: GeoPoint, to: GeoPoint) {
  const earthRadiusM = 6371000;
  const fromLat = toRadians(from.lat);
  const toLat = toRadians(to.lat);
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

  return Math.round(earthRadiusM * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine)));
}

export function estimateParcelRouteMinutes(routeDistanceKm: number) {
  return Math.max(4, Math.round(routeDistanceKm / 18 * 60));
}

export function formatParcelEta(routeMinutes: number) {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(new Date(Date.now() + routeMinutes * 60 * 1000));
}

function getParcelPointScore(point: ParcelPickupPoint) {
  const distanceScore = Math.max(0, 1200 - point.distanceM) / 1200 * 48;
  const detourScore = Math.max(0, 1.2 - point.detourKm) * 28;
  const packageScore = Math.min(point.pendingCount, 4) * 7;

  return distanceScore + detourScore + packageScore;
}

function toRadians(value: number) {
  return value * Math.PI / 180;
}
